<script setup lang="ts">
/* =============================================================
   ITEM ROW
   =============================================================
   One record in an <ItemList>:

     [title]                      [aside]   [actions]
     [meta lines…]

   On a wide screen that is one row, scannable like a table. On a
   phone the actions drop below the text and the aside stays
   beside the title, so nothing important scrolls off sideways --
   which is what a <table> did with the price column.

   With `to`, the whole text block is a router link (and the
   chevron says so); actions stay separate buttons, never nested
   inside the link.
   ============================================================= */

import { RouterLink } from 'vue-router'
import Icon from '@/components/Icon.vue'

defineProps<{
  /** Makes the row open something. */
  to?: string
  /** Dims the row -- a past block, a finished booking. */
  muted?: boolean
}>()
</script>

<template>
  <li class="row" :class="{ muted }">
    <component :is="to ? RouterLink : 'div'" :to="to" class="main" :class="{ link: to }">
      <div class="top">
        <span class="title"><slot name="title" /></span>
        <span v-if="$slots.aside" class="aside"><slot name="aside" /></span>
      </div>
      <div v-if="$slots.default" class="meta"><slot /></div>
    </component>

    <div v-if="$slots.actions" class="actions"><slot name="actions" /></div>
    <Icon v-else-if="to" name="chevronRight" :size="16" class="chevron" />
  </li>
</template>

<style scoped>
.row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  row-gap: var(--space-2xs);
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  font-size: var(--text-sm);
  border-bottom: var(--border-width) solid var(--color-border);
}

.row:last-child {
  border-bottom: 0;
}

.row:hover {
  background-color: var(--color-surface-hover);
}

.muted {
  opacity: 0.6;
}

.main {
  /* Wraps the actions onto their own line only when they no longer fit
     beside ~14rem of text: one trash icon stays put, four buttons drop. */
  flex: 1 1 14rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
  color: inherit;
  text-decoration: none;
}

.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-xs);
}

.title {
  min-width: 0;
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.link:hover .title,
.link:focus-visible .title {
  color: var(--color-accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.link:focus-visible {
  outline: none;
}

.aside {
  flex: none;
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.meta {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  color: var(--color-text-muted);
}

.actions {
  flex: none;
  display: flex;
  gap: var(--space-3xs);
  margin-inline-start: auto;
}

.chevron {
  flex: none;
  color: var(--color-text-faint);
}
</style>
